import React, { useState } from 'react';
import { readUrlParam, useUrlSync } from '../shared/urlState';
import { canOpenModule } from '../shared/biModuleKeys';
import type { ModuleKey } from '../shared/biModuleRegistry';
import { AdminInvoiceObligationsSection } from './adminInvoiceObligationsSection';
import { PaymentSelectionModule } from './paymentSelectionModule';
import { PaymentBatchesModule } from './paymentBatchesModule';
import { ContractsModule } from './contractsModule';
import { BuybackPayoutModule } from './buybackPayoutModule';

type TabKey = 'likvidatura' | 'selection' | 'batches' | 'contracts' | 'payout' | 'settings';
type UserLike = { role?: string; allowed_modules?: string[] } | null;

const TABS: {
    key: TabKey;
    icon: string;
    hr: string;
    en: string;
    moduleKey?: ModuleKey;
    allow: (u: UserLike) => boolean;
}[] = [
    {
        key: 'likvidatura', icon: '🧾', hr: 'Likvidatura', en: 'Liquidation',
        // m.121 (URA/IRA/Virmani) = likvidator/admin posao: admin/superadmin (role) ILI eksplicitan m.121 ACL.
        // (granularan m.121 NIJE u allowed_modules superadmina — zato i role check.)
        allow: (u) => !u
            || u.role === 'superadministrator' || u.role === 'administrator'
            || u.allowed_modules === undefined
            || u.allowed_modules.includes('m.121'),
    },
    { key: 'selection', icon: '📌', hr: 'Odabir plaćanja', en: 'Selection', moduleKey: 'payment_selection', allow: (u) => !u || canOpenModule(u, 'payment_selection') },
    { key: 'batches', icon: '🗓️', hr: 'CF nalozi', en: 'CF batches', moduleKey: 'payment_batches', allow: (u) => !u || canOpenModule(u, 'payment_batches') },
    { key: 'contracts', icon: '📜', hr: 'Ugovori', en: 'Contracts', moduleKey: 'contracts', allow: (u) => !u || canOpenModule(u, 'contracts') },
    { key: 'payout', icon: '💵', hr: 'Isplata otkupa', en: 'Buyback payout', moduleKey: 'buyback_payout', allow: (u) => !u || canOpenModule(u, 'buyback_payout') },
    { key: 'settings', icon: '⚙', hr: 'Postavke', en: 'Settings', moduleKey: 'platni_cf', allow: (u) => !u || u.role === 'superadministrator' },
];

/**
 * m.21.5 Plaćanja & Cash-Flow — jedinstveni radni tok (tabovi):
 *   Likvidatura (m.121 puna funkcionalnost) → Odabir (sektori) → CF nalozi → Ugovori → Isplata otkupa.
 * Pristup po fazi (allow) — tab se prikazuje samo ako korisnik smije; podaci su scoped u backendu.
 */
export function PlatniCfModule({ token, locale, user, onBack }: {
    token: string | null;
    locale: 'hr' | 'en';
    user: UserLike;
    onBack: () => void;
}) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const visible = TABS.filter((tab) => tab.allow(user));
    const tabs = visible.length > 0 ? visible : TABS;
    const [active, setActive] = useState<TabKey>(readUrlParam('tab', tabs[0].key) as TabKey);
    useUrlSync('tab', active, active === tabs[0].key);
    const safeActive = tabs.some((x) => x.key === active) ? active : tabs[0].key;

    return (
        <div style={{ padding: '14px 18px 0', color: '#2f3337' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <button onClick={onBack} style={btnGhost}>← {t('Natrag', 'Back')}</button>
                <h2 style={{ margin: 0, fontSize: 20 }}>💸 {t('Plaćanja & Cash-Flow', 'Payments & Cash-Flow')}</h2>
                <span style={{ fontSize: 12, color: '#8a7a55' }}>
                    {tabs.map((x, i) => <span key={x.key}>{i > 0 ? ' → ' : ''}{locale === 'en' ? x.en : x.hr}</span>)}
                </span>
            </div>

            <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #efe6d2', marginBottom: 6, flexWrap: 'wrap' }}>
                {tabs.map((tab) => {
                    const on = safeActive === tab.key;
                    return (
                        <button
                            key={tab.key}
                            onClick={() => setActive(tab.key)}
                            style={{
                                border: 'none', background: 'transparent', cursor: 'pointer',
                                padding: '9px 16px', fontSize: 14, fontWeight: on ? 700 : 500,
                                color: on ? 'var(--ad-ink)' : '#8a7a55',
                                borderBottom: on ? '3px solid var(--ad-acc)' : '3px solid transparent',
                                marginBottom: -2,
                            }}
                        >
                            {tab.icon} {locale === 'en' ? tab.en : tab.hr}
                        </button>
                    );
                })}
            </div>

            {safeActive === 'likvidatura' ? <LikvidaturaTab token={token ?? ''} locale={locale} /> : null}
            {safeActive === 'selection' ? <PaymentSelectionModule token={token} locale={locale} onBack={onBack} embedded /> : null}
            {safeActive === 'batches' ? <PaymentBatchesModule token={token} locale={locale} onBack={onBack} embedded /> : null}
            {safeActive === 'contracts' ? <ContractsModule token={token} locale={locale} onBack={onBack} embedded /> : null}
            {safeActive === 'payout' ? <BuybackPayoutModule token={token} locale={locale} onBack={onBack} embedded /> : null}
            {safeActive === 'settings' ? <LikvidaturaSettings token={token ?? ''} locale={locale} /> : null}
        </div>
    );
}

type LikvUser = { id: number; name: string; email: string; role: string };

/** Likvidatura postavke — izbor ovlaštenih Uprava korisnika (označavaju URA) + operatera (kreira naloge). */
function LikvidaturaSettings({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [users, setUsers] = useState<LikvUser[]>([]);
    const [uprava, setUprava] = useState<number[]>([]);
    const [operater, setOperater] = useState<number[]>([]);
    const [threshold, setThreshold] = useState('');
    const [signers, setSigners] = useState<number[]>([]);
    const [batchDual, setBatchDual] = useState('');
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);

    const load = React.useCallback(() => {
        fetch('/api/likvidatura/settings', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.json())
            .then((d) => { setUsers(d.users ?? []); setUprava(d.uprava_user_ids ?? []); setOperater(d.operater_user_ids ?? []); if (d.second_signature_threshold_eur != null) setThreshold(String(d.second_signature_threshold_eur)); setSigners(d.second_signer_user_ids ?? []); if (d.batch_dual_threshold_eur != null) setBatchDual(String(d.batch_dual_threshold_eur)); })
            .catch(() => setMsg('Greška pri učitavanju.'));
    }, [token]);
    React.useEffect(() => { load(); }, [load]);

    const toggle = (list: number[], set: (v: number[]) => void, id: number) =>
        set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

    const save = async () => {
        setBusy(true); setMsg('');
        try {
            const res = await fetch('/api/likvidatura/settings', {
                method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ uprava_user_ids: uprava, operater_user_ids: operater, second_signature_threshold_eur: threshold.trim() === '' ? null : Number(String(threshold).replace(',', '.')), second_signer_user_ids: signers, batch_dual_threshold_eur: batchDual.trim() === '' ? null : Number(String(batchDual).replace(',', '.')) }),
            });
            setMsg(res.ok ? t('Spremljeno.', 'Saved.') : t('Greška pri spremanju.', 'Save error.'));
        } catch { setMsg(t('Greška pri spremanju.', 'Save error.')); }
        setBusy(false);
    };

    const picker = (title: string, hint: string, list: number[], set: (v: number[]) => void) => (
        <div style={{ flex: 1, minWidth: 280 }}>
            <h4 style={{ margin: '0 0 4px' }}>{title}</h4>
            <p style={{ fontSize: 12, color: '#8a7a55', margin: '0 0 8px' }}>{hint}</p>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e0d6bf', borderRadius: 8, padding: 6 }}>
                {users.map((u) => (
                    <label key={u.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 4px', fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={list.includes(u.id)} onChange={() => toggle(list, set, u.id)} />
                        <span>{u.name} <span style={{ color: '#aaa' }}>· {u.role}</span></span>
                    </label>
                ))}
            </div>
        </div>
    );

    return (
        <div style={{ padding: '4px 2px' }}>
            <p style={{ fontSize: 13, color: '#8a7a55' }}>{t('Ovlaštenja se biraju iz korisnika. Uprava označava otvorene URA za plaćanje; operater kreira zbirne naloge.', 'Authorization is chosen from users. Management marks open payables; operator creates payment batches.')}</p>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                {picker('🧾 ' + t('Prvi potpisnik (PLATI = 1. potpis)', 'First signer (PAY = 1st sign)'), t('Vidi sve otvorene URA, klikom PLATI zadaje plaćanje i daje 1. potpis.', 'Sees all open payables; PAY click = 1st signature.'), uprava, setUprava)}
                {picker('🗓️ ' + t('Izvršitelj (skida virmane)', 'Executor (downloads wires)'), t('Slaže zbirni nalog i preuzima virmane (HUB3/SEPA).', 'Assembles the batch and downloads wires.'), operater, setOperater)}
                {picker('✍ ' + t('Supotpisnik (2. potpis)', 'Co-signer (2nd sign)'), t('Daju 2. potpis na račun iznad praga. Zasebna lista od prvih potpisnika; prazno = samo superadmin.', 'Give the 2nd signature above threshold. Separate from first signers; empty = superadmin only.'), signers, setSigners)}
            </div>
            <div style={{ marginTop: 16 }}>
                <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>✍ {t('Prag za 2. potpis (€)', '2nd-signature threshold (€)')}</label>
                <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} style={{ width: 160, padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2' }} />
                <span style={{ fontSize: 12, color: '#8a7a55', marginLeft: 10 }}>{threshold.trim() === '' ? t('Nije postavljen: dok se ne spremi, 2. potpis treba svaki račun. ', 'Not set: until saved, every invoice needs a 2nd signature. ') : ''}{t('Po računu. Račun iznad ovog iznosa bez 2. potpisa ne ulazi u nalog ni virmane.', 'Per invoice. Above this, no 2nd signature means no batch and no wires.')}</span>
            </div>
            <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{t('Prag za dvije ovjere naloga (€)', 'Dual batch approval threshold (€)')}</label>
                <input type="number" value={batchDual} onChange={(e) => setBatchDual(e.target.value)} style={{ width: 160, padding: '7px 10px', borderRadius: 8, border: '1px solid #d8cdb2' }} />
                <span style={{ fontSize: 12, color: '#8a7a55', marginLeft: 10 }}>{t('Zbir naloga iznad ovog iznosa traži dvije ovjere prije virmana.', 'Batch total above this needs two approvals before wires.')}</span>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
                <button onClick={save} disabled={busy} style={{ padding: '8px 18px', background: 'var(--ad-acc)', color: '#fff', border: 0, borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>{t('Spremi', 'Save')}</button>
                {msg ? <span style={{ fontWeight: 600 }}>{msg}</span> : null}
            </div>
        </div>
    );
}

type LikvSub = '1211' | '1212' | '1213';

/**
 * Likvidatura tab — puna m.121 funkcionalnost (URA / IRA / Virmani) sa sub-navom.
 *  URA (1211) = obveze prema dobavljačima (uvoz + šifra narudžbe + knjiženje); IRA (1212) = potraživanja;
 *  Virmani (1213) = trajni nalozi / ugovorna plaćanja.
 */
function LikvidaturaTab({ token, locale }: { token: string; locale: 'hr' | 'en' }) {
    const [sub, setSub] = useState<LikvSub>('1211');
    const subs: { id: LikvSub; hr: string; en: string }[] = [
        { id: '1211', hr: 'URA — ulazni računi', en: 'APA — incoming' },
        { id: '1212', hr: 'IRA — izlazni računi', en: 'ARA — outgoing' },
        { id: '1213', hr: 'Virmani / trajni', en: 'Standing payments' },
    ];

    return (
        <div>
            <div style={{ display: 'flex', gap: 6, margin: '4px 0 12px', flexWrap: 'wrap' }}>
                {subs.map((s) => {
                    const on = sub === s.id;
                    return (
                        <button
                            key={s.id}
                            onClick={() => setSub(s.id)}
                            style={{
                                cursor: 'pointer', borderRadius: 999, fontSize: 12.5, fontWeight: 600,
                                padding: '5px 13px',
                                border: on ? '1px solid var(--ad-acc)' : '1px solid #e0d6bf',
                                background: on ? 'rgba(var(--ad-acc-rgb),0.16)' : '#fffdf6',
                                color: on ? 'var(--ad-ink)' : '#8a7a55',
                            }}
                        >
                            {locale === 'en' ? s.en : s.hr}
                        </button>
                    );
                })}
            </div>
            <div className="likv-embed">
                <style>{`
                    .likv-embed .table-wrap { overflow-x: auto; }
                    .likv-embed .admin-user-table { min-width: 980px; }
                `}</style>
                <AdminInvoiceObligationsSection token={token} locale={locale} reloadTick={0} invoiceAdminSection={sub} />
            </div>
        </div>
    );
}

const btnGhost: React.CSSProperties = { background: '#fffdf6', color: '#2f3337', border: '1px solid #d8cdb2', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13 };
